import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Mic, Bot, Zap, Mail, MessageSquare, Rss, Bell, Archive, Inbox, Plus } from 'lucide-react'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ChannelStatus = 'active' | 'coming_soon' | 'disabled'

type Channel = {
  id: string
  label: string
  description: string
  icon: React.ReactNode
  status: ChannelStatus
}

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

const CHANNELS: Channel[] = [
  {
    id: 'voice_memos',
    label: 'Voice Memos',
    description: 'Processed recordings appear in Inbox for review, filing, or chat.',
    icon: <Mic className="h-4 w-4" />,
    status: 'active',
  },
  {
    id: 'agent_messages',
    label: 'Agent Messages',
    description: 'Maater agents deliver summaries, digests, and task outputs here.',
    icon: <Bot className="h-4 w-4" />,
    status: 'active',
  },
  {
    id: 'flow_jobs',
    label: 'Flow Job Outputs',
    description: 'Results from scheduled jobs (weekly triage, daily digest, etc.) land here.',
    icon: <Zap className="h-4 w-4" />,
    status: 'active',
  },
  {
    id: 'email',
    label: 'Email',
    description: 'Route emails to your Inbox via a personal Maater address.',
    icon: <Mail className="h-4 w-4" />,
    status: 'coming_soon',
  },
  {
    id: 'sms',
    label: 'SMS / Telegram / Slack',
    description: 'Receive messages from messaging apps directly in your Inbox.',
    icon: <MessageSquare className="h-4 w-4" />,
    status: 'coming_soon',
  },
  {
    id: 'rss',
    label: 'RSS / Webhooks',
    description: 'Subscribe to external feeds or receive webhook payloads as inbox items.',
    icon: <Rss className="h-4 w-4" />,
    status: 'coming_soon',
  },
]

const statusColors: Record<ChannelStatus, string> = {
  active: 'bg-green-500/10 text-green-500 border-green-500/20',
  coming_soon: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
  disabled: 'bg-gray-500/10 text-gray-500 border-gray-500/20',
}

const statusLabels: Record<ChannelStatus, string> = {
  active: 'Active',
  coming_soon: 'Coming soon',
  disabled: 'Disabled',
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function InboxSettings() {
  return (
    <div className="space-y-8">

      {/* Channels */}
      <section>
        <h3 className="text-base font-semibold mb-0.5">Channels</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Sources that feed items into your Inbox.
        </p>
        <div className="space-y-3">
          {CHANNELS.map(channel => (
            <div
              key={channel.id}
              className="flex items-start justify-between gap-4 rounded-lg px-4 py-3 bg-muted/30"
            >
              <div className="flex items-start gap-3">
                <span className="mt-0.5 text-muted-foreground shrink-0">{channel.icon}</span>
                <div>
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-medium">{channel.label}</span>
                    <Badge className={`text-[10px] px-1.5 py-0 border ${statusColors[channel.status]}`}>
                      {statusLabels[channel.status]}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{channel.description}</p>
                </div>
              </div>
              {channel.status === 'active' && (
                <Switch defaultChecked className="shrink-0 mt-0.5" />
              )}
              {channel.status === 'coming_soon' && (
                <Button variant="outline" size="sm" disabled className="shrink-0 text-xs">
                  <Plus className="h-3 w-3 mr-1" />
                  Connect
                </Button>
              )}
            </div>
          ))}
        </div>
      </section>

      <Separator />

      {/* Notifications */}
      <section>
        <h3 className="text-base font-semibold mb-0.5">Notifications</h3>
        <p className="text-sm text-muted-foreground mb-4">
          How Maater alerts you when new items arrive.
        </p>
        <div className="space-y-4">
          <SettingRow
            icon={<Bell className="h-4 w-4" />}
            label="Toast notifications"
            description="Show a brief pop-up when a new inbox item arrives."
            defaultChecked
          />
          <SettingRow
            icon={<Inbox className="h-4 w-4" />}
            label="Badge on Inbox nav item"
            description="Show unread count on the Inbox sidebar entry."
            defaultChecked
          />
        </div>
      </section>

      <Separator />

      {/* Retention */}
      <section>
        <h3 className="text-base font-semibold mb-0.5">Retention</h3>
        <p className="text-sm text-muted-foreground mb-4">
          How long dismissed and archived items are kept.
        </p>
        <div className="space-y-4">
          <SettingRow
            icon={<Archive className="h-4 w-4" />}
            label="Auto-archive read items"
            description="Automatically archive items after you've opened them."
            defaultChecked={false}
            placeholder
          />
          <SettingRow
            icon={<Archive className="h-4 w-4" />}
            label="Purge archived after 30 days"
            description="Permanently delete archived items older than 30 days."
            defaultChecked={false}
            placeholder
          />
        </div>
      </section>

    </div>
  )
}

// ---------------------------------------------------------------------------
// Small helper
// ---------------------------------------------------------------------------

function SettingRow({
  icon,
  label,
  description,
  defaultChecked,
  placeholder,
}: {
  icon: React.ReactNode
  label: string
  description: string
  defaultChecked: boolean
  placeholder?: boolean
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 text-muted-foreground shrink-0">{icon}</span>
        <div>
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium">{label}</p>
            {placeholder && (
              <Badge className="text-[10px] px-1.5 py-0 border bg-gray-500/10 text-gray-500 border-gray-500/20">
                Placeholder
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </div>
      <Switch defaultChecked={defaultChecked} disabled={placeholder} className="shrink-0" />
    </div>
  )
}
